// Bloque `propuesta_diagnostico` del Configurador (CONFIGURADOR.md · Orión #392):
//   { tipo:'propuesta_diagnostico', contenido:'<resumen en texto>', propuesta:{ tipo, enfoque, nombre,
//     objetivo, cliente, temas_obligatorios:[{tema,norma,numeral,fuente_url}], temas_sugeridos:[{tema,fundamento}],
//     participantes_sugeridos:[str] } }
// Normalización en el borde (port del panel de public-sites components/diagnostico/chat/propuesta.ts):
// lo que llega del modelo se valida aquí y la tarjeta solo recibe la forma limpia.

export interface Cita {
  norma: string;
  numeral: string;
  fuente_url: string;
  verificado_el: string;
  titulo: string;
}
export interface TemaObligatorio extends Cita {
  tema: string;
}
export interface Propuesta {
  tipo: string;
  enfoque: string;
  nombre: string;
  objetivo: string;
  cliente: string;
  temas_obligatorios: TemaObligatorio[];
  temas_sugeridos: Array<{ tema: string; fundamento: string }>;
  participantes_sugeridos: string[];
}

const s = (v: unknown, max = 2000) => (typeof v === 'string' ? v.trim().slice(0, max) : typeof v === 'number' ? String(v) : '');

/** Solo http(s): una URL de fuente rara no se vuelve enlace. */
export function safeUrl(v: unknown): string {
  const u = s(v, 2048);
  if (!u) return '';
  try {
    const x = new URL(u);
    return x.protocol === 'https:' || x.protocol === 'http:' ? x.toString() : '';
  } catch {
    return '';
  }
}

function normCita(x: any): Cita {
  return {
    norma: s(x?.norma, 200),
    numeral: s(x?.numeral, 60),
    fuente_url: safeUrl(x?.fuente_url ?? x?.url),
    verificado_el: s(x?.verificado_el, 40),
    titulo: s(x?.titulo ?? x?.title, 300),
  };
}

/** `bloque.propuesta` (o, por compatibilidad, `contenido` si llegara como objeto/JSON). */
export function parsePropuesta(bloque: any): Propuesta | null {
  let raw: any = bloque?.propuesta;
  if (!raw && bloque?.contenido && typeof bloque.contenido === 'object') raw = bloque.contenido;
  if (!raw && typeof bloque?.contenido === 'string' && bloque.contenido.trim().startsWith('{')) {
    try {
      raw = JSON.parse(bloque.contenido);
    } catch {
      raw = null;
    }
  }
  if (!raw || typeof raw !== 'object') return null;
  const arr = (v: unknown) => (Array.isArray(v) ? v : []);
  const p: Propuesta = {
    tipo: s(raw.tipo, 60),
    enfoque: s(raw.enfoque, 300),
    nombre: s(raw.nombre, 200),
    objetivo: s(raw.objetivo, 1500),
    cliente: s(raw.cliente, 200),
    temas_obligatorios: arr(raw.temas_obligatorios)
      .map((t: any) => (typeof t === 'string' ? { tema: s(t, 300), ...normCita({}) } : { tema: s(t?.tema, 300), ...normCita(t) }))
      .filter((t) => t.tema),
    temas_sugeridos: arr(raw.temas_sugeridos)
      .map((t: any) => (typeof t === 'string' ? { tema: s(t, 300), fundamento: '' } : { tema: s(t?.tema, 300), fundamento: s(t?.fundamento, 600) }))
      .filter((t) => t.tema),
    participantes_sugeridos: arr(raw.participantes_sugeridos).map((x: any) => s(x, 200)).filter(Boolean),
  };
  return p.nombre || p.objetivo || p.tipo ? p : null;
}

/** Resumen en texto del bloque (siempre texto en el sobre; Orión #392). */
export function resumenDe(bloque: any): string {
  return typeof bloque?.contenido === 'string' && !bloque.contenido.trim().startsWith('{') ? bloque.contenido.trim() : '';
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «NOM-002-STPS-2010 · 5.3 · verificado 24 oct» */
export function citaLabel(c: Cita): string {
  let ver = '';
  const m = c.verificado_el.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) ver = `verificado ${Number(m[3])} ${MESES[Number(m[2]) - 1] || ''}`.trim();
  else if (c.verificado_el) ver = `verificado ${c.verificado_el}`;
  return [c.norma || c.titulo, c.numeral, ver].filter(Boolean).join(' · ');
}

/** Objetivo del proyecto con los temas (el backend no tiene campo de temas: van en el objetivo). */
export function objetivoConTemas(p: Propuesta): string {
  const parts = [p.objetivo];
  if (p.temas_obligatorios.length)
    parts.push(
      'Temas obligatorios: ' +
        p.temas_obligatorios.map((t) => `${t.tema}${t.norma ? ` (${[t.norma, t.numeral].filter(Boolean).join(' ')})` : ''}`).join('; ') +
        '.',
    );
  if (p.temas_sugeridos.length) parts.push('Temas sugeridos: ' + p.temas_sugeridos.map((t) => t.tema).join('; ') + '.');
  return parts.filter(Boolean).join('\n\n').slice(0, 4000);
}

// ── Tipos de diagnóstico (mismo catálogo y alias que el panel) ──
export type TipoDiagnostico =
  | 'capacitacion' | 'implementacion_agente' | 'producto' | 'organizacional' | 'rrhh' | 'dnc' | 'manual_puestos' | 'nom035';

const TIPOS: Record<TipoDiagnostico, { label: string; pideEnfoque: boolean }> = {
  capacitacion: { label: 'Capacitación', pideEnfoque: false },
  implementacion_agente: { label: 'Implementación de agente', pideEnfoque: true },
  producto: { label: 'Desarrollo de producto', pideEnfoque: true },
  organizacional: { label: 'Diagnóstico organizacional', pideEnfoque: false },
  rrhh: { label: 'Desarrollo organizacional', pideEnfoque: false },
  dnc: { label: 'Capacitación · DNC', pideEnfoque: false },
  manual_puestos: { label: 'Manual de puestos', pideEnfoque: false },
  nom035: { label: 'NOM-035', pideEnfoque: false },
};

const ALIAS: Record<string, TipoDiagnostico> = {
  capacitacion: 'capacitacion',
  implementacion_agente: 'implementacion_agente', implementacion_de_agente: 'implementacion_agente', implementacion: 'implementacion_agente', agente: 'implementacion_agente',
  producto: 'producto', desarrollo_de_producto: 'producto',
  organizacional: 'organizacional', diagnostico_organizacional: 'organizacional',
  rrhh: 'rrhh', rh: 'rrhh', recursos_humanos: 'rrhh', diagnostico_de_recursos_humanos: 'rrhh', personas: 'rrhh', talento: 'rrhh', desarrollo_organizacional: 'rrhh',
  dnc: 'dnc', deteccion_de_necesidades_de_capacitacion: 'dnc', deteccion_de_necesidades: 'dnc', capacitacion_dnc: 'dnc',
  manual_puestos: 'manual_puestos', manual_de_puestos: 'manual_puestos', perfil_de_puesto: 'manual_puestos', perfiles_de_puesto: 'manual_puestos', descripcion_de_puestos: 'manual_puestos',
  nom035: 'nom035', nom_035: 'nom035', nom_035_stps_2018: 'nom035',
};

const fold = (v: unknown) =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');

/** Valor del modelo/API → tipo conocido. Vacío o desconocido = capacitación. */
export function normTipo(v: unknown): TipoDiagnostico {
  return ALIAS[fold(v)] ?? 'capacitacion';
}
export function tipoDef(v: unknown) {
  return TIPOS[normTipo(v)];
}

export interface DiagService {
  slug: string;
  nombre: string;
  tipo: string;
}

/** Servicios DISPONIBLES de `GET /diagnostics/catalogo` (acepta `{lineas:[{servicios}]}` o lista plana). */
export function serviciosDisponibles(raw: any): DiagService[] {
  const lineas = Array.isArray(raw?.lineas) ? raw.lineas : null;
  const flat: any[] = lineas
    ? lineas.flatMap((l: any) => (Array.isArray(l?.servicios) ? l.servicios : []))
    : Array.isArray(raw) ? raw : Array.isArray(raw?.servicios) ? raw.servicios : [];
  return flat
    .filter((x) => fold(x?.estado ?? x?.status) === 'disponible')
    .map((x) => ({ slug: s(x?.slug, 60), nombre: s(x?.nombre ?? x?.name, 200), tipo: s(x?.tipo, 60) }))
    .filter((x) => x.slug && x.nombre);
}

/** El servicio del catálogo que corresponde a la propuesta (por slug exacto o por tipo). */
export function servicioDe(p: Propuesta, servicios: DiagService[]): DiagService | null {
  const raw = p.tipo.trim().toLowerCase();
  const t = normTipo(p.tipo);
  return servicios.find((x) => x.slug.toLowerCase() === raw) || servicios.find((x) => normTipo(x.tipo || x.slug) === t) || null;
}
